/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Grid, LoadingSection, Tag, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowPreviousIcon } from '@coursera/cds-icons';
import type { Product } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';
import FullscreenDialog from '@coursera/event-pulse/react/FullscreenDialog';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import SimplifiedHeader from 'bundles/composable-page-header-consumer/header-renderers/SimplifiedHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import { featureCardStyles, getValueProps } from 'bundles/coursera-plus-common/components/elevateLTV/featureCardUtils';
import PrePurchaseUpsellAllPlansPricingTable, {
  allPlansProductsValues,
} from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellAllPlansPricingTable';
import type { AllPlansProductsType } from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellAllPlansPricingTable';
import PrePurchaseUpsellCourseraPlusEnrollButton from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellCourseraPlusEnrollButton';
import PrePurchaseUpsellEnrollButton from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellEnrollButton';
import { getAllPlansModalCopy } from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellEnrollCopies';
import { logo } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import {
  useGetProductPricesAndConfig,
  usePrePurchaseUpsellEnrollment,
} from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { getEnrollModalName, getProductInfo } from 'bundles/enroll-modal/utils/eventingUtils';

import _t from 'i18n!nls/coursera-plus-common';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const { HeadingGroup, Content, Actions } = FullscreenDialog;

const styles = {
  allPlansDialog: css`
    /* 
      Disable the 'skip to main content' button on the header embedded in the dialog,
      because the main content area isn't visible while the dialog is open.
    */
    .skip-button-outer-div {
      display: none;
    }
  `,

  dialogContent: css`
    @media (min-width: ${breakpoints.values.md}px) and (max-width: ${breakpoints.values.lg}px) {
      padding: 0 var(--cds-spacing-200);
    }
  `,
  pageHeader: css`
    text-align: center;
    padding-top: 81px;
    margin-bottom: 74px;
    padding-bottom: 0;

    ${breakpoints.down('sm')} {
      margin-bottom: 0;
      padding: var(--cds-spacing-600) var(--cds-spacing-100);
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-400) var(--cds-spacing-100);
    }
  `,

  backButtonContainer: css`
    max-width: 1442px; /* Match the page header width */
    margin: 0 auto;

    ${breakpoints.down('sm')} {
      padding: 0 var(--cds-spacing-300);
    }
  `,

  mobileEnrollActions: css`
    ${breakpoints.down('xs')} {
      box-shadow: 0 -4px 4px 0 rgb(0 0 0 / 13%);
    }

    ${breakpoints.up('xs')} {
      display: flex;
      justify-content: center;
    }

    ${breakpoints.up('sm')} {
      padding: var(--cds-spacing-600) var(--cds-spacing-200) !important;
    }
  `,

  mobileEnrollButton: css`
    ${breakpoints.up('sm')} {
      max-width: 200px;
    }
  `,
  singleProgramCustomStyles: (marginTop: number) => css`
    margin-top: ${marginTop}px;
  `,

  comparisonTableContainer: css`
    max-width: 700px;
    margin: 0 auto;
  `,

  ...featureCardStyles,
};

export type InputProps = {
  onClose?: () => void;
  isStandAloneCourse?: boolean;
  showSingleProgram?: boolean;
  s12nPriceDisplay?: React.JSX.Element | null;
  standaloneCoursePriceDisplay?: React.JSX.Element;
  courseraPlusMonthlyPriceDisplay: React.JSX.Element | null;
  courseraPlusAnnualPriceAsMonthlyDisplay: React.JSX.Element | null;
  courseraPlusAnnualPriceDisplay: React.JSX.Element | null;
  courseraPlusAnnualDiscountPercent?: number | null;
  courseraPlusAnnualProductItemId?: string;
  courseraPlusMonthlyProductItemId?: string;
  enrollInCourse?: () => Promise<{}>;
  enrollInSubscription?: () => Promise<{}>;
  hasCourseraPlusFreeTrial: boolean;
  isGatewayCertificate?: boolean;
  hasS12nFreeTrial?: boolean;
  s12nId?: string;
  courseId?: string;
  eventingProduct?: Product;
};

export const PrePurchaseUpsellAllPlansModal = ({
  onClose,
  isStandAloneCourse = false,
  showSingleProgram = true,
  s12nPriceDisplay,
  standaloneCoursePriceDisplay,
  courseraPlusMonthlyPriceDisplay,
  courseraPlusAnnualPriceAsMonthlyDisplay,
  courseraPlusAnnualPriceDisplay,
  courseraPlusAnnualDiscountPercent,
  courseraPlusAnnualProductItemId,
  courseraPlusMonthlyProductItemId,
  enrollInCourse,
  enrollInSubscription,
  hasCourseraPlusFreeTrial,
  isGatewayCertificate,
  hasS12nFreeTrial,
  s12nId,
  courseId,
  eventingProduct,
}: InputProps) => {
  const [selectedProduct, setSelectedProduct] = useState<AllPlansProductsType>(allPlansProductsValues.PLUS_MONTHLY);
  const track = useTracker();
  const eventingModalName = getEnrollModalName({ isPrePurchaseUpsellAllPlansShown: true })!;
  const copy = getAllPlansModalCopy();
  const showCoachAccess = !isGatewayCertificate;

  const isScreenSmUp = useMediaQuery(breakpoints.up('sm'));
  const isScreenMdUp = useMediaQuery(breakpoints.up('md'));

  const programPriceDisplay = isStandAloneCourse ? standaloneCoursePriceDisplay : s12nPriceDisplay;

  const isLoading = !(
    courseraPlusMonthlyPriceDisplay &&
    courseraPlusAnnualPriceAsMonthlyDisplay &&
    courseraPlusAnnualPriceDisplay &&
    courseraPlusAnnualDiscountPercent &&
    courseraPlusMonthlyProductItemId &&
    courseraPlusAnnualProductItemId &&
    (showSingleProgram ? programPriceDisplay : true)
  );
  if (isLoading) {
    return <LoadingSection label={_t('Fetching data...')} />;
  }

  const courseraPlusAnnualDiscountCopy = _t('Save #{courseraPlusAnnualDiscountPercent}%', {
    courseraPlusAnnualDiscountPercent,
  });

  const getSingleProgramEnrollCtaCopy = () => {
    if (isStandAloneCourse) {
      return copy.paidEnrollCta;
    }
    return hasS12nFreeTrial ? copy.freeTrialEnrollCta : copy.paidEnrollCta;
  };

  const getCourseraPlusEnrollCtaCopy = () => {
    return hasCourseraPlusFreeTrial ? copy.freeTrialEnrollCta : copy.paidEnrollCta;
  };

  const getCourseraPlusEnrollCtaCopyForA11yAnnouncer = (product: AllPlansProductsType) => {
    if (product === allPlansProductsValues.PLUS_MONTHLY) {
      return hasCourseraPlusFreeTrial ? copy.freeTrialEnrollCtaCPlusMonthlyA11y : copy.paidEnrollCtaCPlusMonthlyA11y;
    } else if (product === allPlansProductsValues.PLUS_ANNUAL) {
      return hasCourseraPlusFreeTrial ? copy.freeTrialEnrollCtaCPlusAnnualA11y : copy.paidEnrollCtaCPlusAnnualA11y;
    } else if (product === allPlansProductsValues.SINGLE_PROGRAM) {
      return hasS12nFreeTrial ? copy.freeTrialEnrollCtaSingleProgramA11y : copy.paidEnrollCtaSingleProgramA11y;
    }

    return getCourseraPlusEnrollCtaCopy();
  };

  const handleClose = () => {
    track('click_button', {
      button: {
        name: 'dismiss',
      },
      modal: {
        name: eventingModalName,
      },
      product: eventingProduct,
    });
    onClose?.();
  };

  const handleSingleProgramEnrollment = async () => {
    if (isStandAloneCourse) {
      if (!enrollInCourse) {
        throw new Error('Cannot enroll in course, enrollInCourse function is not defined');
      }
      return enrollInCourse();
    } else {
      if (!enrollInSubscription) {
        throw new Error('Cannot enroll in subscription, enrollInSubscription function is not defined');
      }
      return enrollInSubscription();
    }
  };

  const courseraPlusButtonAdditionalProps =
    selectedProduct === allPlansProductsValues.PLUS_MONTHLY
      ? {
          trackingName: 'pre_purchase_upsell_coursera_plus_monthly_enroll_cta',
          isMonthly: true,
          annualProductItemId: courseraPlusAnnualProductItemId,
          productItemId: courseraPlusMonthlyProductItemId,
        }
      : {
          productItemId: courseraPlusAnnualProductItemId,
          trackingName: 'pre_purchase_upsell_coursera_plus_annual_enroll_cta',
        };

  const MobileEnrollButton = () => {
    return selectedProduct === allPlansProductsValues.SINGLE_PROGRAM ? (
      <PrePurchaseUpsellEnrollButton
        variant="primary"
        onCtaClick={handleSingleProgramEnrollment}
        size="medium"
        fullWidth
        css={styles.mobileEnrollButton}
        data-testid="single-program-enroll-button"
        trackingName="pre_purchase_upsell_single_program_enroll_cta"
      >
        {_t('Enroll')}
      </PrePurchaseUpsellEnrollButton>
    ) : (
      <PrePurchaseUpsellCourseraPlusEnrollButton
        css={styles.mobileEnrollButton}
        variant="primary"
        size="medium"
        fullWidth
        s12nId={s12nId}
        courseId={courseId}
        {...courseraPlusButtonAdditionalProps}
      >
        {_t('Enroll')}
      </PrePurchaseUpsellCourseraPlusEnrollButton>
    );
  };

  const comparedProducts = [
    ...(showSingleProgram
      ? [
          {
            key: 'singleProgram',
            dataTestid: 'single-program-card',
            title: (
              <Typography2 component="div" variant="subtitleLarge">
                {copy.singleProgramTitle}
              </Typography2>
            ),
            supportText: isStandAloneCourse ? (
              <FormattedMessage
                message={_t('{priceDisplay} one-time purchase')}
                priceDisplay={standaloneCoursePriceDisplay}
              />
            ) : (
              <FormattedMessage message={_t('{priceDisplay} billed monthly')} priceDisplay={s12nPriceDisplay} />
            ),
            valueProps: getValueProps({ isCourseraPlus: false }),
            enrollCta: (
              <PrePurchaseUpsellEnrollButton
                variant="secondary"
                onCtaClick={handleSingleProgramEnrollment}
                ariaLabelText={getCourseraPlusEnrollCtaCopyForA11yAnnouncer(allPlansProductsValues.SINGLE_PROGRAM)}
                size="small"
                fullWidth
                data-testid="single-program-enroll-button"
                trackingName="pre_purchase_upsell_single_program_enroll_cta"
              >
                {getSingleProgramEnrollCtaCopy()}
              </PrePurchaseUpsellEnrollButton>
            ),
          },
        ]
      : []),
    {
      key: 'plusMonthly',
      dataTestid: 'coursera-plus-monthly-card',
      isTaggedFeatureCard: true,
      isPrimaryFeatureCard: true,
      tag: copy.mostPopularTag,
      title: (
        <React.Fragment>
          <Imgix src={logo.PLUS_FULL_BLUE} maxHeight={14} alt={_t('Coursera Plus')} />
          <Typography2 component="span" variant="subtitleLarge">
            {_t('monthly')}
          </Typography2>
        </React.Fragment>
      ),
      supportText: (
        <FormattedMessage
          message={_t('{monthlyPriceDisplay} billed monthly')}
          monthlyPriceDisplay={courseraPlusMonthlyPriceDisplay}
        />
      ),
      valueProps: getValueProps({ isCourseraPlus: true, showCoachAccess }),
      enrollCta: (
        <PrePurchaseUpsellCourseraPlusEnrollButton
          trackingName="pre_purchase_upsell_coursera_plus_monthly_enroll_cta"
          variant="primary"
          fullWidth
          size="small"
          s12nId={s12nId}
          isMonthly={true}
          annualProductItemId={courseraPlusAnnualProductItemId}
          courseId={courseId}
          productItemId={courseraPlusMonthlyProductItemId}
          ariaLabelText={getCourseraPlusEnrollCtaCopyForA11yAnnouncer(allPlansProductsValues.PLUS_MONTHLY)}
        >
          {getCourseraPlusEnrollCtaCopy()}
        </PrePurchaseUpsellCourseraPlusEnrollButton>
      ),
    },
    {
      key: 'plusAnnual',
      dataTestid: 'coursera-plus-annual-card',
      isTaggedFeatureCard: true,
      tag: courseraPlusAnnualDiscountCopy,
      title: (
        <React.Fragment>
          <Imgix src={logo.PLUS_FULL_BLUE} maxHeight={14} alt={_t('Coursera Plus')} />
          <Typography2 component="span" variant="subtitleLarge">
            {_t('annual')}
          </Typography2>
        </React.Fragment>
      ),
      supportText: (
        <FormattedMessage
          message={_t('{monthlyPriceDisplay}/mo billed annually at {annualPriceDisplay}')}
          monthlyPriceDisplay={courseraPlusAnnualPriceAsMonthlyDisplay}
          annualPriceDisplay={courseraPlusAnnualPriceDisplay}
        />
      ),
      valueProps: getValueProps({ isCourseraPlus: true, showCoachAccess }),
      enrollCta: (
        <PrePurchaseUpsellCourseraPlusEnrollButton
          productItemId={courseraPlusAnnualProductItemId}
          s12nId={s12nId}
          courseId={courseId}
          trackingName="pre_purchase_upsell_coursera_plus_annual_enroll_cta"
          ariaLabelText={getCourseraPlusEnrollCtaCopyForA11yAnnouncer(allPlansProductsValues.PLUS_ANNUAL)}
          variant="secondary"
          size="small"
          fullWidth
        >
          {getCourseraPlusEnrollCtaCopy()}
        </PrePurchaseUpsellCourseraPlusEnrollButton>
      ),
    },
  ];

  return (
    <FullscreenDialog
      aria-describedby="dialog-content"
      open={true}
      onClose={handleClose}
      sticky={isScreenSmUp ? 'none' : 'actions'}
      trackingData={{ modal: { name: eventingModalName }, product: eventingProduct }}
      css={styles.allPlansDialog}
    >
      <SimplifiedHeader enableCourseraLogoLink={false} />
      <UniversalPageFeatures />
      <Content css={styles.backButtonContainer}>
        <TrackedButton
          trackingName="pre_purchase_upsell_all_plans_back_button"
          icon={<ArrowPreviousIcon />}
          iconPosition="before"
          size="small"
          variant="ghost"
          edgeAlign="start"
          css={css`
            margin-top: var(--cds-spacing-400);
          `}
          onClick={handleClose}
        >
          {copy.back}
        </TrackedButton>
      </Content>
      <HeadingGroup heading={copy.pageHeader} css={styles.pageHeader} />
      <Content css={styles.dialogContent}>
        {isScreenMdUp ? (
          <Grid container justifyContent="center" css={styles.featureCardsWrapper}>
            <Grid item container justifyContent="center" css={styles.featureCardRow}>
              {comparedProducts.map(
                ({ title, dataTestid, supportText, isPrimaryFeatureCard, isTaggedFeatureCard, tag, key }) => (
                  <Grid
                    item
                    data-testid={dataTestid}
                    key={key}
                    css={[
                      styles.featureCardCell,
                      styles.featureCardHeaderCell,
                      isPrimaryFeatureCard && styles.primaryFeatureCardCell,
                      isTaggedFeatureCard && styles.taggedFeatureCardCell,
                    ]}
                  >
                    <Grid item>
                      {tag && (
                        <Tag
                          {...(isPrimaryFeatureCard
                            ? {
                                color: 'highlight1',
                                variant: 'status',
                              }
                            : { variant: 'category' })}
                          priority="primary"
                          aria-label={tag}
                          css={styles.featureTag}
                        >
                          {tag}
                        </Tag>
                      )}
                      <div css={styles.featureCardHeaderContainer}>
                        <div css={styles.featureCardLogoHeader}>{title}</div>
                        <Typography2 component="div" variant="bodyPrimary" color="supportText" align="center">
                          {supportText}
                        </Typography2>
                      </div>
                    </Grid>
                  </Grid>
                )
              )}
            </Grid>
            <Grid item container justifyContent="center" css={styles.featureCardRow}>
              {comparedProducts.map(({ valueProps, isPrimaryFeatureCard, isTaggedFeatureCard, key }) => (
                <Grid
                  item
                  key={key}
                  css={[
                    styles.featureCardCell,
                    isPrimaryFeatureCard && styles.primaryFeatureCardCell,
                    isTaggedFeatureCard && styles.taggedFeatureCardCell,
                  ]}
                >
                  {valueProps}
                </Grid>
              ))}
            </Grid>
            <Grid item container justifyContent="center" css={styles.featureCardRow}>
              {comparedProducts.map(({ enrollCta, isPrimaryFeatureCard, isTaggedFeatureCard, key }) => (
                <Grid
                  item
                  key={key}
                  css={[
                    styles.featureCardCell,
                    isPrimaryFeatureCard && styles.primaryFeatureCardCell,
                    isTaggedFeatureCard && styles.taggedFeatureCardCell,
                  ]}
                >
                  {enrollCta}
                </Grid>
              ))}
            </Grid>
          </Grid>
        ) : (
          <div css={styles.comparisonTableContainer}>
            <PrePurchaseUpsellAllPlansPricingTable
              isStandaloneCourse={isStandAloneCourse}
              showSingleProgram={showSingleProgram}
              hasCourseraPlusFreeTrial={hasCourseraPlusFreeTrial}
              hasS12nFreeTrial={hasS12nFreeTrial}
              standaloneCoursePriceDisplay={standaloneCoursePriceDisplay}
              s12nPriceDisplay={s12nPriceDisplay}
              courseraPlusMonthlyPriceDisplay={courseraPlusMonthlyPriceDisplay}
              courseraPlusAnnualPriceDisplay={courseraPlusAnnualPriceDisplay}
              setSelectedProduct={setSelectedProduct}
              defaultSelectedProduct={allPlansProductsValues.PLUS_MONTHLY}
              isGatewayCertificate={Boolean(isGatewayCertificate)}
            />
          </div>
        )}
      </Content>

      {!isScreenMdUp && (
        <Actions css={styles.mobileEnrollActions}>
          <MobileEnrollButton />
        </Actions>
      )}
    </FullscreenDialog>
  );
};

// Should only be used when the enroll PageDataProvider is available in context
const PrePurchaseUpsellAllPlansModalWithHooks = ({
  onClose,
  isStandAloneCourse = false,
  isFromS12nSelection = false,
  isGatewayCertificate = false,
  showSingleProgram = true,
}: {
  onClose?: () => void;
  isStandAloneCourse?: boolean;
  isFromS12nSelection?: boolean;
  isGatewayCertificate?: boolean;
  showSingleProgram?: boolean;
}) => {
  const { s12n, course, isSpecialization } = usePageData();
  const {
    s12nPriceDisplay,
    standaloneCoursePriceDisplay,
    courseraPlusMonthlyPriceDisplay,
    courseraPlusAnnualPriceAsMonthlyDisplay,
    courseraPlusAnnualPriceDisplay,
    courseraPlusAnnualDiscountPercent,
    courseraPlusAnnualProductItemId,
    courseraPlusMonthlyProductItemId,
  } = useGetProductPricesAndConfig();
  const {
    enrollInCourse,
    enrollInSubscription,
    hasCourseraPlusFreeTrial,
    canEnrollThroughS12nSubscriptionFreeTrial: hasS12nFreeTrial,
  } = usePrePurchaseUpsellEnrollment({ isFromS12nSelection, isPrePurchaseUpsellAllPlansShown: true });

  const eventingProduct = getProductInfo({
    isSpecialization,
    s12n,
    course,
  });

  return (
    <PrePurchaseUpsellAllPlansModal
      onClose={onClose}
      isStandAloneCourse={isStandAloneCourse}
      showSingleProgram={showSingleProgram}
      s12nPriceDisplay={s12nPriceDisplay}
      standaloneCoursePriceDisplay={standaloneCoursePriceDisplay}
      courseraPlusMonthlyPriceDisplay={courseraPlusMonthlyPriceDisplay}
      courseraPlusAnnualPriceAsMonthlyDisplay={courseraPlusAnnualPriceAsMonthlyDisplay}
      courseraPlusAnnualPriceDisplay={courseraPlusAnnualPriceDisplay}
      courseraPlusAnnualDiscountPercent={courseraPlusAnnualDiscountPercent}
      courseraPlusAnnualProductItemId={courseraPlusAnnualProductItemId}
      courseraPlusMonthlyProductItemId={courseraPlusMonthlyProductItemId}
      enrollInCourse={enrollInCourse}
      enrollInSubscription={enrollInSubscription}
      hasCourseraPlusFreeTrial={hasCourseraPlusFreeTrial}
      isGatewayCertificate={isGatewayCertificate}
      hasS12nFreeTrial={hasS12nFreeTrial}
      s12nId={s12n?.id}
      courseId={course?.id}
      eventingProduct={eventingProduct}
    />
  );
};
export default PrePurchaseUpsellAllPlansModalWithHooks;
